// ============================================================================
// useCanAccessSourcing — the frontend mirror of the DB SECURITY DEFINER
// function public.can_access_sourcing() (2026-07-02).
//
// The Sourcing module is gated by a go-live feature flag + allowlist that
// lives in the DB (tcs_information.sourcing_module_status + the Jack/Antonela
// allowlist inside can_access_sourcing()). We call the RPC rather than
// re-deriving the rule client-side so the nav/routes can never drift from the
// RLS enforcement — flipping the module live for AMs is a DB data flip, and
// the UI follows with zero code change.
//
// Module-level cache: the answer is per-session stable (role + flag), and the
// hook is consumed by the sidebar, mobile nav, hub overview, and the ⌘K
// palette simultaneously — one RPC, everyone shares it. Defaults to false
// until resolved so we never flash a nav entry the RLS would reject.
// ============================================================================

import { useEffect, useState } from 'react';
import { getSupabaseToken, supabase } from './supabase';

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<(v: boolean) => void>();

// The bridged JWT arrives asynchronously (Firebase auth → jwt-bridge edge fn
// → setSupabaseToken). This hook fires at first sidebar mount, which reliably
// beats that round-trip — so the RPC used to execute as ANON, auth.uid() was
// NULL, can_access_sourcing() legitimately returned false, and the module
// cache pinned false for the whole session (2026-07-07 bug: Ben/Jack/Antonela
// all locked out of a gate the DB grants them). Rule: never resolve — and
// NEVER cache — until we hold a bridged token; never cache an RPC error.
async function waitForBridgedToken(maxMs = 15000): Promise<boolean> {
  const start = Date.now();
  while (!getSupabaseToken()) {
    if (Date.now() - start > maxMs) return false;
    await new Promise((r) => setTimeout(r, 250));
  }
  return true;
}

async function resolve(): Promise<boolean> {
  if (cached !== null) return cached;
  if (!inflight) {
    inflight = (async () => {
      let result = false;
      try {
        const authed = await waitForBridgedToken();
        if (authed) {
          const { data, error } = await supabase.rpc('can_access_sourcing');
          if (!error) {
            // Only a real, authenticated answer is cacheable.
            cached = Boolean(data);
            result = cached;
          }
        }
        // No token in time, or RPC error: report false for THIS render but
        // leave `cached` null so the next mount re-asks instead of pinning
        // a transient failure for the session.
      } catch {
        /* leave cached null — retry on next mount */
      }
      inflight = null;
      const answer = cached ?? result;
      for (const fn of listeners) fn(answer);
      return answer;
    })();
  }
  return inflight;
}

/** Test / sign-out hook: drop the cached answer so the next mount re-asks. */
export function resetSourcingAccessCache(): void {
  cached = null;
}

export function useCanAccessSourcing(): { canAccess: boolean; loading: boolean } {
  const [canAccess, setCanAccess] = useState(cached ?? false);
  const [loading, setLoading] = useState(cached === null);

  useEffect(() => {
    if (cached !== null) {
      setCanAccess(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const listener = (v: boolean) => {
      if (!cancelled) {
        setCanAccess(v);
        setLoading(false);
      }
    };
    listeners.add(listener);
    void resolve();
    return () => {
      cancelled = true;
      listeners.delete(listener);
    };
  }, []);

  return { canAccess, loading };
}
